import { createFileRoute } from "@tanstack/react-router";
import { ServicePage } from "@/components/ServicePage";
import { RelatedServicesLinks } from "@/components/RelatedServicesLinks";
import {
  Phone,
  Search,
  Target,
  BarChart3,
  Filter,
  MessageSquare,
  Wrench,
  Truck,
  Home as HomeIcon,
  Hammer,
  Trees,
  Zap,
  HardHat,
  MapPin,
} from "lucide-react";

export const Route = createFileRoute("/lead-generation-colorado")({
  head: () => ({
    meta: [
        { name: "description", content: "Colorado lead generation that fills your pipeline. Local SEO, paid ads, and conversion-built landing pages from a Colorado Springs team since 2006." },
      { title: "Lead Generation Colorado | Local Lead Gen Agency" },
      {
        name: "description",
        content:
          "Colorado lead generation built for home-service & local businesses. SEO, Google Ads, Maps, and conversion-tuned websites that ring the phone — month-to-month, no contracts.",
      },
      { property: "og:title", content: "Lead Generation Colorado | Local Lead Gen Agency" },
    ],
    links: [
      { rel: "canonical", href: "https://coloradowebimpressions.com/lead-generation-colorado" },
    ],
  }),
  component: () => (
    <ServicePage
      slug="lead-generation"
      serviceName="Lead Generation"
      pricingRoute="/pricing"
      eyebrow="Colorado Lead Generation"
      title="Colorado Lead Generation That Actually Rings the Phone"
      goldWord="Rings the Phone"
      description="A complete Colorado lead-generation system — local SEO, Google Maps, conversion-tuned web design, and Google Ads — built so you own the pipeline instead of renting leads from HomeAdvisor and Angi."
      intro={{
        heading: "Real lead generation, not shared leads from a national platform.",
        body: [
          "Most Colorado businesses calling themselves 'lead generation' clients are actually paying HomeAdvisor, Angi, or Thumbtack to share the same lead with three competitors. The margin disappears, the homeowner is annoyed, and the business owner is one bad month away from churn.",
          "We build the opposite: an owned lead pipeline using local SEO, Google Maps, conversion-built web design, and (when it makes sense) Google Ads — tuned to your specific Colorado service area, your real capacity, and the actual jobs you want more of. Every lead comes to you exclusively.",
        ],
      }}
      benefits={[
        "Exclusive leads — not shared with competitors",
        "Call tracking on every channel",
        "Map Pack visibility in your Colorado cities",
        "Conversion-tuned landing pages",
        "Google Ads & LSA when paid pays back",
        "Transparent monthly reporting tied to revenue",
      ]}
      features={[
        { icon: Search, title: "Organic SEO", desc: "Page-one rankings for the buying-intent keywords your Colorado customers actually search." },
        { icon: MapPin, title: "Google Maps Ranking", desc: "Top-3 Map Pack visibility in every Colorado city you serve." },
        { icon: Target, title: "Google Ads & LSA", desc: "Paid search managed in-house — call-only campaigns, geo-fenced budgets, and full transparency." },
        { icon: Filter, title: "Conversion-Built Pages", desc: "Sticky click-to-call, fast load times, and trust signals that turn traffic into booked jobs." },
        { icon: Phone, title: "Call Tracking", desc: "See exactly which channel — and which keyword — produced every phone call and form fill." },
        { icon: BarChart3, title: "Revenue-Tied Reporting", desc: "Plain-English monthly reports showing leads, calls, jobs booked, and ROI — not vanity metrics." },
      ]}
      frameworkHeading="The Colorado Lead Generation Method"
      frameworkIntro="A 5-step playbook for building an owned, exclusive Colorado lead pipeline."
      framework={[
        { number: "01", title: "Pipeline Audit", desc: "We map your current lead sources, conversion rates, and revenue per lead. Identify what's leaking and what's working." },
        { number: "02", title: "Channel Strategy", desc: "Pick the right mix — SEO, Maps, paid, content — based on your Colorado market, capacity, and timeline. We don't sell what you don't need." },
        { number: "03", title: "Conversion Foundation", desc: "Fix the website first. Sticky call buttons, fast mobile load, real trust signals. Driving traffic to a broken site is wasted spend." },
        { number: "04", title: "Channel Build", desc: "Local SEO, Map Pack work, and (when it pays back) Google Ads & LSA — built around your real Colorado service area." },
        { number: "05", title: "Track, Report, Refine", desc: "Call tracking on every channel, monthly reports tied to revenue, and quarterly strategy reviews to compound results." },
      ]}
      industries={[
        { icon: Wrench, name: "HVAC", desc: "Furnace, AC, and heat-pump leads from real Colorado homeowners." },
        { icon: Truck, name: "Plumbing", desc: "Emergency, water heater, and remodel leads — exclusive to you." },
        { icon: HomeIcon, name: "Roofing", desc: "Insurance-claim and storm-response leads in every Colorado market." },
        { icon: Hammer, name: "General Contractors", desc: "Remodel and build leads with real budgets attached." },
        { icon: Trees, name: "Landscaping", desc: "Design/build, hardscape, and recurring maintenance leads." },
        { icon: HardHat, name: "Concrete & Excavation", desc: "Commercial and residential project leads at high ticket." },
        { icon: Zap, name: "Electrical", desc: "Panel upgrades, EV chargers, and emergency service leads." },
      ]}
      faqs={[
        { q: "How is this different from HomeAdvisor or Angi?", a: "Every lead is exclusive — only your business sees it. You own the channel, you own the relationship, and you keep your margin instead of paying per shared lead." },
        { q: "How long until I see real lead flow?", a: "Google Ads and LSA can produce calls within the first week. SEO and Map Pack flow typically build over 60–120 days, then compound. Most clients run a paid + organic mix for that reason." },
        { q: "Will you guarantee a number of leads per month?", a: "No ethical agency does, because lead volume depends on your market, season, capacity, and competition. What we DO promise is exclusive leads, full call tracking, and transparent reporting on every channel." },
        { q: "Can you handle Google Ads and SEO together?", a: "Yes — we build them together so they reinforce each other instead of cannibalizing. One owner-led team, one pipeline, one report." },
        { q: "Do you only serve Colorado businesses?", a: "Yes. We're a Colorado-only lead-generation agency. Every campaign is tuned to Colorado Springs, Denver, Pueblo, Fort Collins, and the surrounding Front Range markets." },
      ]}
      sectionsAfterPricing={<RelatedServicesLinks current="seo-services" />}
    />
  ),
});
